import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { VideoLoader } from '@/components/ui/video-loader';
import { cn } from '@/lib/utils';

import defaultConfig from '../config/video-modal-config.json';
import { useVideoModal } from '../hooks/useVideoModal';
import { VideoModalProps } from '../types';

/**
 * VideoModal Component
 *
 * A modal dialog that displays a video with optional title, message, and action button.
 * Used primarily for onboarding flows and confirmation screens.
 *
 * Features:
 * - Auto-plays video when modal opens
 * - Optional close button
 * - Customizable title, message, and button text
 * - Video end callback support
 * - Loading state with VideoLoader integration
 * - Responsive design
 *
 * @example
 * ```tsx
 * <VideoModal
 *   isOpen={showIntro}
 *   onClose={() => setShowIntro(false)}
 *   videoUrl="/videos/welcome.mp4"
 *   title="Welcome!"
 *   message="Let's get started"
 *   buttonText="Continue"
 * />
 * ```
 */
export const VideoModal = ({
  isOpen,
  onClose,
  onStart,
  title,
  message,
  buttonText = defaultConfig.defaults.buttonText,
  showCloseButton = defaultConfig.defaults.showCloseButton,
  videoUrl,
  onVideoEnd,
  maxWidth = '3xl',
  autoPlay = defaultConfig.defaults.autoPlay,
}: VideoModalProps) => {
  const { videoRef, isLoading, handleVideoEnded, handleLoadedData } = useVideoModal(
    isOpen,
    videoUrl,
    onVideoEnd,
    autoPlay
  );

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          maxWidthClasses[maxWidth],
          defaultConfig.styles.background,
          `border-${defaultConfig.styles.borderColor}`,
          defaultConfig.animation.modalScale
        )}
      >
        {showCloseButton && (
          <button
            onClick={onClose}
            className="absolute right-4 top-4 z-10 rounded-full p-2 transition-colors hover:bg-muted"
            aria-label={defaultConfig.defaults.closeButtonAriaLabel}
          >
            <X className="h-5 w-5" />
          </button>
        )}

        <div className="space-y-6 p-6">
          {/* Video */}
          <div className="relative flex aspect-video items-center justify-center overflow-hidden rounded-lg bg-white">
            {isLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <VideoLoader size="large" />
              </div>
            )}
            <video
              ref={videoRef}
              src={videoUrl}
              className={cn(
                'h-full w-full transition-opacity duration-500 ease-in-out',
                `object-${defaultConfig.video.objectFit}`,
                isLoading ? 'opacity-0' : 'opacity-100'
              )}
              onLoadedData={handleLoadedData}
              onEnded={handleVideoEnded}
              playsInline
              autoPlay={autoPlay}
            />
          </div>

          {/* Content */}
          {(title || message) && (
            <div className="space-y-3 text-center">
              {title && <h2 className="text-2xl font-bold text-card-foreground">{title}</h2>}
              {message && <p className="text-lg text-muted-foreground">{message}</p>}
            </div>
          )}

          {/* Action button */}
          <div className="flex justify-center">
            <Button
              onClick={onStart || onClose}
              className="bg-accent px-8 py-6 text-lg font-medium text-accent-foreground hover:bg-accent/90"
            >
              {buttonText}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
